import { useState, useEffect } from 'react';

type UseSessionStorageReturnType<T> = [T | null, (value: T) => void, () => void];

/**
 * Custom hook to manage sessionStorage with React state synchronization and auto-expiry
 * @param key - The key for the sessionStorage item
 * @param initialValue - The initial value to set if the item doesn't exist
 * @param expiryTime - Expiry time in milliseconds (default: 1 day)
 * @returns [storedValue, setValue, removeValue]
 */
export function useSessionStorage<T>(
  key: string,
  initialValue: T,
  expiryTime: number = 24 * 60 * 60 * 1000 // 1 day in milliseconds
): UseSessionStorageReturnType<T> {
  const [storedValue, setStoredValue] = useState<T | null>(() => {
    try {
      const item = window.sessionStorage.getItem(key);
      if (item) {
        const { value, timestamp } = JSON.parse(item);
        const currentTime = new Date().getTime();

        // If data is expired, remove it
        if (currentTime - timestamp > expiryTime) {
          window.sessionStorage.removeItem(key);
          return initialValue;
        }
        return value;
      }
      return initialValue;
    } catch (error) {
      console.error('Error reading from sessionStorage', error);
      return initialValue;
    }
  });

  // Function to set a new value in sessionStorage and update state
  const setValue = (value: T) => {
    try {
      const dataToStore = {
        value,
        timestamp: new Date().getTime()
      };
      setStoredValue(value);
      window.sessionStorage.setItem(key, JSON.stringify(dataToStore));
    } catch (error) {
      console.error('Error setting sessionStorage', error);
    }
  };

  // Function to remove the item from sessionStorage and reset state
  const removeValue = () => {
    try {
      setStoredValue(null);
      window.sessionStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing sessionStorage item', error);
    }
  };

  // Effect to check expiry on mount
  useEffect(() => {
    const item = window.sessionStorage.getItem(key);
    if (item) {
      const { timestamp } = JSON.parse(item);
      const currentTime = new Date().getTime();

      if (currentTime - timestamp > expiryTime) {
        removeValue();
      }
    }
  }, [key, expiryTime]);

  return [storedValue, setValue, removeValue];
}
